"use client";

import { GET_TEMPLATE, type GetTemplateResponse, type TemplateParamsInput, type TemplateQueryInput } from "@/gql/getTemplate";
import { cn } from "@/utils/cn";
import { escapeHtml } from "@/utils/security";
import { useQuery } from "@apollo/client";
import React from "react";
import { Group, Text } from "react-aria-components";
const defParams: TemplateParamsInput = {
  body: {
    customer: "Customer",
    commission: {
      type: "p",
      value: 1.9,
      includesGst: true
    },
    examplePrice: "3 Crore"
  },
  subject: {},
  tempParams: {}
};
interface EmailOrSmsProps {
  query: TemplateQueryInput;
  showSignature?: boolean;
  _isCustomerBuyer?: boolean;
  _dependencies?: unknown[];
  params?: TemplateParamsInput;
  showSubject?: boolean;
  htmlWrapper?: Record<string, unknown>;
  templateData?: {
    body?: string;
    subject?: string;
    react?: boolean;
  };
  config?: {
    enabled?: boolean;
  };
  containerStyle?: React.CSSProperties;
  shouldShowLoader?: boolean;
  className?: string;
  getSubjectAndBodyOnly?: boolean;
}
export default function EmailOrSms({
  query,
  showSignature = false,
  params: propParams,
  showSubject = true,
  htmlWrapper = {},
  templateData = {},
  containerStyle = {
    marginTop: "1rem"
  },
  shouldShowLoader = true,
  className,
  getSubjectAndBodyOnly = false
}: EmailOrSmsProps) {
  const params = propParams || defParams;
  const {
    data,
    loading,
    error
  } = useQuery<GetTemplateResponse>(GET_TEMPLATE, {
    variables: {
      params: {
        getTemplateData: {
          params,
          query,
          getSubjectAndBodyOnly
        }
      }
    },
    errorPolicy: "all"
  });

  // Enhanced debugging (only in development)
  // if (process.env.NODE_ENV === "development") {
  // 	// eslint-disable-next-line no-console
  // 	console.log("Query variables:", {
  // 		triggerHooks: query.triggerHooks,
  // 		type: query.type,
  // 		params: JSON.stringify(params),
  // 	});
  // 	// eslint-disable-next-line no-console
  // 	console.log("Apollo result:", { data, loading, error });
  // 	// eslint-disable-next-line no-console
  // 	console.log("Raw data structure:", JSON.stringify(data, null, 2));
  // 	if (error) {
  // 		// eslint-disable-next-line no-console
  // 		console.log("Network error details:", error.networkError);
  // 		// eslint-disable-next-line no-console
  // 		console.log("GraphQL errors:", error.graphQLErrors);
  // 	}
  // }

  if (loading && shouldShowLoader) {
    return <div className="animate-pulse space-y-2">
				<div className="h-4 bg-gray-200 rounded w-3/4"></div>
				<div className="h-4 bg-gray-200 rounded w-1/2"></div>
			</div>;
  }
  if (error || !data && !loading) {
    // Create a fallback template based on the query type
    const createFallbackTemplate = () => {
      const queryType = query.triggerHooks || query.type || "default";
      const customerName = escapeHtml(params.body?.customer || "Customer");
      const commissionRaw = params.body?.commission?.value || params.body?.examplePrice || "Amount";
      const commission = escapeHtml(String(commissionRaw));
      let fallbackSubject = "";
      let fallbackBody = "";
      if (queryType.includes("confirmation")) {
        fallbackSubject = "Confirmation - Your Request";
        fallbackBody = `
					<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
						<h2 style="color: #2c5aa0;">Thank you for your request!</h2>
						<p>Dear ${customerName},</p>
						<p>We have received your request and our team will get back to you shortly.</p>
						${commission ? `<p><strong>Amount:</strong> ${commission}</p>` : ""}
						<p>Thank you for choosing our services.</p>
						<br/>
						<p>Best regards,<br/>Settlin Team</p>
					</div>
				`;
      } else if (queryType.includes("mail")) {
        fallbackSubject = "Important Update";
        fallbackBody = `
					<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
						<p>Dear ${customerName},</p>
						<p>This is an important update regarding your request.</p>
						${commission ? `<p><strong>Reference:</strong> ${commission}</p>` : ""}
						<p>Please contact us if you have any questions.</p>
						<br/>
						<p>Best regards,<br/>Settlin Team</p>
					</div>
				`;
      } else {
        fallbackSubject = "Notification";
        fallbackBody = `
					<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
						<p>Dear ${customerName},</p>
						<p>We wanted to keep you updated on your request.</p>
						${commission ? `<p><strong>Details:</strong> ${commission}</p>` : ""}
						<p>Thank you for your patience.</p>
						<br/>
						<p>Best regards,<br/>Settlin Team</p>
					</div>
				`;
      }
      return {
        subject: fallbackSubject,
        body: fallbackBody
      };
    };

    // Use fallback template or provided templateData
    const templateResult = data?.template?.getTemplateData || createFallbackTemplate();
    const {
      body,
      subject
    } = templateResult;
    return <Group className={cn("space-y-4", className)} style={containerStyle}>
				{error && process.env.NODE_ENV === "development" && <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-2">
						⚠️ Using fallback template due to GraphQL error
					</div>}
				{showSubject && Boolean(subject) && <div className="flex flex-wrap gap-1">
						<Text className="font-bold">Sub:</Text>
						<Text>{subject}</Text>
					</div>}
				<div className="email-body">
					<div className="show-list-style email-body prose max-w-none" {...htmlWrapper} dangerouslySetInnerHTML={{
          __html: body || ""
        }} />
				</div>
			</Group>;
  }
  if ((params as {
    buttonDisabledMsg?: string;
  }).buttonDisabledMsg) {
    return <div className="bg-destructive/15 text-destructive px-4 py-3 rounded-md border border-destructive/20">
				<Text className="text-sm font-medium">{(params as {
          buttonDisabledMsg: string;
        }).buttonDisabledMsg}</Text>
			</div>;
  }
  const templateResult_0 = data?.template?.getTemplateData || templateData;

  // If no template data is available, create a basic fallback
  if (!templateResult_0 || !templateResult_0.body && !templateResult_0.subject) {
    const customerName_0 = escapeHtml(params.body?.customer || "Customer");
    const queryType_0 = query.triggerHooks || query.type || "default";
    const commissionRaw_0 = params.body?.commission?.value || params.body?.examplePrice;
    const commission_0 = commissionRaw_0 ? escapeHtml(String(commissionRaw_0)) : undefined;
    const fallbackTemplate = {
      subject: queryType_0.includes("confirmation") ? "Confirmation - Your Request" : "Thank you for your interest",
      body: `
				<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px;">
					<h2 style="color: #2c5aa0;">
						${queryType_0.includes("confirmation") ? "Thank you for your request!" : "Thank you for your interest"}
					</h2>
					<p>Dear ${customerName_0},</p>
					<p>
						${queryType_0.includes("confirmation") ? "We have received your request and our team will get back to you shortly." : "Thank you for your interest in our services. We will get back to you soon."}
					</p>
					${commission_0 ? `<p><strong>Amount:</strong> ${commission_0}</p>` : ""}
					<p>Thank you for choosing our services.</p>
					<br/>
					<p>Best regards,<br/>Settlin Team</p>
				</div>
			`
    };
    Object.assign(templateResult_0 || {}, fallbackTemplate);
  }
  const {
    react: isReact,
    body: body_0,
    subject: subject_0
  } = templateResult_0 || {};
  let sub: React.ReactNode, html: React.ReactNode, signature: string | undefined;
  if (isReact && subject_0 && body_0) {
    try {
      sub = subject_0 && new Function("React", `return props => ${subject_0}`)(React)(params.subject);
      html = body_0 && new Function("React", `return props => ${body_0}`)(React)(params.body);
    } catch {
      sub = subject_0;
      html = body_0;
    }
  } else {
    sub = subject_0;
    html = body_0;
  }
  if (showSignature) {
    signature = "<div>Signature placeholder</div>"; // Placeholder for now
  }
  return <>
			<style jsx>{`
				whatsapp {
					width: 300px;
				}
				whatsapp > object[type="video/mp4"] {
					width: 300px;
					height: 150px;
					margin: auto;
				}
				whatsapp > object[type="image/jpg"] {
					width: 300px;
					height: 400px;
					margin: auto;
				}
				whatsapp[content-type="media"] {
					max-width: 50%;
					display: inline-flex;
					flex-direction: column;
				}
				whatsapp[content-type="location"] {
					max-width: 50%;
					display: inline-flex;
					flex-direction: column;
					align-items: center;
				}
				whatsapp {
					display: block;
					margin: 1rem 0;
				}
				whatsapp > iframe {
					width: 300px;
					height: 150px;
					margin: auto;
					border: none;
				}
				whatsapp > object {
					text-align: center;
				}
				whatsapp h6 {
					font-weight: 300;
					font-size: 0.8rem;
					display: block;
					margin: 0.5rem 0;
				}
			`}</style>
			<Group className={cn("space-y-4", className)} style={containerStyle}>
				{showSubject && Boolean(sub) && <div className="flex flex-wrap gap-1">
						<Text className="font-bold">Sub:</Text>
						<Text>{sub}</Text>
					</div>}

				<div className="email-body">
					{isReact ? <div className="show-list-style email-body prose max-w-none" {...htmlWrapper}>
							{html}
							{showSignature && signature && <div dangerouslySetInnerHTML={{
            __html: signature
          }} />}
						</div> : <div className="show-list-style email-body prose max-w-none" {...htmlWrapper} dangerouslySetInnerHTML={{
          __html: body_0 || ""
        }} />}
				</div>
			</Group>
		</>;
}